import LoadingSpinner from "@app/shared/LoadingSpinner";
import { FC, useState, useEffect, useCallback, useContext, JSX } from "react";
import { ColumnConfig, SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { ModalLink } from "@app/shared/Modals";
import { CodeMapSetModelWithActiveProjects, CodeMapTypeEnum } from "@app/codemaps/CodeMapSetModels";
import { MemberSetModelWithActiveProjects } from "@app/members/memberSetModels";
import { CodeMapSetService } from "@app/codemaps";
import { ProviderSetModelWithActiveProjects } from "@app/provider-sets/providerSet";
import { toast } from "react-toastify";
import { UserContext } from "@app/user/UserContext";
import Roles from "@app/user/roles";
import { preventDefaultMouseEvent } from "@app/shared/utility/preventDefaultMouseEvent";
import MemberSetService from "@app/members/memberSetService";
import ProviderSetService from "@app/provider-sets/providerSetService";
import { EditTemplateOptionsDetailModel } from "@app/admin/manage-templates/template";

interface EditTemplateOptionsTableData {
  key: string;
  name: string;
  activeProjects: number;
  isArchived: boolean;
  lastUpdated: string;
  hide: JSX.Element;
  [wafflesKeyColumnName]: string;
}

/* oxlint-disable sort-keys */
const clientCountyColumnConfig: ColumnConfig = {
  name: {
    displayText: "Client County Classifications",
    sizeContentToFit: true,
    testId: "client-county-classification-name",
  },
  activeProjects: {
    displayText: "Active Projects",
    sizeContentToFit: true,
    testId: "client-county-classification-active-projects",
  },
  lastUpdated: {
    displayText: "Last Updated",
    sizeContentToFit: true,
    testId: "client-county-classification-last-updated",
  },
  hide: {
    displayText: "",
    sizeContentToFit: true,
    testId: "client-county-classification-hide",
  }
};

const universalCountyColumnConfig: ColumnConfig = {
  name: {
    displayText: "Universal County Classifications",
    sizeContentToFit: true,
    testId: "unversal-county-classification-name",
  },
  activeProjects: {
    displayText: "Active Projects",
    sizeContentToFit: true,
    testId: "unversal-county-classification-active-projects",
  },
  lastUpdated: {
    displayText: "Last Updated",
    sizeContentToFit: true,
    testId: "unversal-county-classification-last-updated",
  },
  hide: {
    displayText: "",
    sizeContentToFit: true,
    testId: "unversal-county-classification-hide",
  }
};

const clientMarketColumnConfig: ColumnConfig = {
  name: {
    displayText: "Client Markets",
    sizeContentToFit: true,
    testId: "client-market-name",
  },
  activeProjects: {
    displayText: "Active Projects",
    sizeContentToFit: true,
    testId: "client-market-active-projects",
  },
  lastUpdated: {
    displayText: "Last Updated",
    sizeContentToFit: true,
    testId: "client-market-last-updated",
  },
  hide: {
    displayText: "",
    sizeContentToFit: true,
    testId: "client-market-hide",
  }
};

const universalMarketColumnConfig: ColumnConfig = {
  name: {
    displayText: "Universal Markets",
    sizeContentToFit: true,
    testId: "universal-market-name",
  },
  activeProjects: {
    displayText: "Active Projects",
    sizeContentToFit: true,
    testId: "universal-market-active-projects",
  },
  lastUpdated: {
    displayText: "Last Updated",
    sizeContentToFit: true,
    testId: "universal-market-last-updated",
  },
  hide: {
    displayText: "",
    sizeContentToFit: true,
    testId: "universal-market-hide",
  }
};

const clientMemberColumnConfig: ColumnConfig = {
  name: {
    displayText: "Client Members",
    sizeContentToFit: true,
    testId: "client-member-name",
  },
  activeProjects: {
    displayText: "Active Projects",
    sizeContentToFit: true,
    testId: "client-member-active-projects",
  },
  lastUpdated: {
    displayText: "Last Updated",
    sizeContentToFit: true,
    testId: "client-member-last-updated",
  },
  hide: {
    displayText: "",
    sizeContentToFit: true,
    testId: "client-member-hide",
  }
};

const universalMemberColumnConfig: ColumnConfig = {
  name: {
    displayText: "Universal Members",
    sizeContentToFit: true,
    testId: "universal-member-name",
  },
  activeProjects: {
    displayText: "Active Projects",
    sizeContentToFit: true,
    testId: "universal-member-active-projects",
  },
  lastUpdated: {
    displayText: "Last Updated",
    sizeContentToFit: true,
    testId: "universal-member-last-updated",
  },
  hide: {
    displayText: "",
    sizeContentToFit: true,
    testId: "universal-member-hide",
  }
};

const clientProviderSetColumnConfig: ColumnConfig = {
  name: {
    displayText: "Client Provider Sets",
    sizeContentToFit: true,
    testId: "client-provider-sets-name",
  },
  activeProjects: {
    displayText: "Active Projects",
    sizeContentToFit: true,
    testId: "client-provider-sets-active-projects",
  },
  lastUpdated: {
    displayText: "Last Updated",
    sizeContentToFit: true,
    testId: "client-provider-sets-last-updated",
  },
  hide: {
    displayText: "",
    sizeContentToFit: true,
    testId: "client-provider-sets-hide",
  }
};

const universalProviderSetsColumnConfig: ColumnConfig = {
  name: {
    displayText: "Universal Provider Sets",
    sizeContentToFit: true,
    testId: "universal-provider-sets-name",
  },
  activeProjects: {
    displayText: "Active Projects",
    sizeContentToFit: true,
    testId: "universal-provider-sets-active-projects",
  },
  lastUpdated: {
    displayText: "Last Updated",
    sizeContentToFit: true,
    testId: "universal-provider-sets-last-updated",
  },
  hide: {
    displayText: "",
    sizeContentToFit: true,
    testId: "universal-provider-sets-hide",
  }
};
/* oxlint-enable */

const EditTemplateOptionsFlyout: FC = (): JSX.Element => {
  const [loading, setLoading] = useState<boolean>(true);
  const [currentTab, setCurrentTab] = useState<string>("County");
  const [clientCountyClassifications, setClientCountyClassifications] = useState<EditTemplateOptionsTableData[]>([]);
  const [clientCountyClassificationsSelected, setClientCountyClassificationsSelected] = useState<EditTemplateOptionsTableData[]>([]);
  const [universalCountyClassifications, setUniversalCountyClassifications] = useState<EditTemplateOptionsTableData[]>();
  const [universalCountyClassificationsSelected, setUniversalCountyClassificationsSelected] = useState<EditTemplateOptionsTableData[]>([]);
  const [clientMarkets, setClientMarkets] = useState<EditTemplateOptionsTableData[]>([]);
  const [clientMarketsSelected, setClientMarketsSelected] = useState<EditTemplateOptionsTableData[]>([]);
  const [universalMarkets, setUniversalMarkets] = useState<EditTemplateOptionsTableData[]>([]);
  const [universalMarketsSelected, setUniversalMarketsSelected] = useState<EditTemplateOptionsTableData[]>([]);
  const [clientMembers, setClientMembers] = useState<EditTemplateOptionsTableData[]>([]);
  const [clientMembersSelected, setClientMembersSelected] = useState<EditTemplateOptionsTableData[]>([]);
  const [universalMembers, setUniversalMembers] = useState<EditTemplateOptionsTableData[]>([]);
  const [universalMembersSelected, setUniversalMembersSelected] = useState<EditTemplateOptionsTableData[]>([]);
  const [clientProviderSets, setProviderSets] = useState<EditTemplateOptionsTableData[]>([]);
  const [clientProviderSetsSelected, setClientProviderSetsSelected] = useState<EditTemplateOptionsTableData[]>([]);
  const [universalProviderSets, setUniversalProviderSets] = useState<EditTemplateOptionsTableData[]>([]);
  const [universalProviderSetsSelected, setUniversalProviderSetsSelected] = useState<EditTemplateOptionsTableData[]>([]);

  const [universalCountyCodeMaps, setUniversalCountyCodeMaps] = useState<EditTemplateOptionsDetailModel[]>([]);
  const [universalSpecialtyCodeMaps, setUniversalSpecialtyCodeMaps] = useState<EditTemplateOptionsDetailModel[]>([]);
  const [clientSpecificCountyCodeMaps, setClientSpecificCountyCodeMaps] = useState<EditTemplateOptionsDetailModel[]>([]);
  const [clientSpecificSpecialtyCodeMaps, setClientSpecificSpecialtyCodeMaps] = useState<EditTemplateOptionsDetailModel[]>([]);
  const [clientProviderData, setClientProviderData] = useState<EditTemplateOptionsDetailModel[]>([]);
  const [universalProviderData, setUniversalProviderData] = useState<EditTemplateOptionsDetailModel[]>([]);
  const [memberSetData, setMemberSetData] = useState<EditTemplateOptionsDetailModel[]>([]);
  const [universalMemberSetData, setUniversalMemberSetData] = useState<EditTemplateOptionsDetailModel[]>([]);

  const [refetchData, setRefetchData] = useState<boolean>(true);

  const codeMapSetService = new CodeMapSetService();
  const providerSetService = new ProviderSetService();
  const memberSetService  = new MemberSetService();

  const user = useContext(UserContext);
  const [isUniversalTemplateManager] = useState(user.roles.some(x => x === Roles.UNIVERSAL_TEMPLATE_MANAGER));

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const countyAndMarketCodeMapTypes = [CodeMapTypeEnum.CountyClassMap, CodeMapTypeEnum.SpecialtyCodeMap]; 
      /* oxlint-disable sort-keys */
      let universalCountyCodeMaps: EditTemplateOptionsDetailModel[] = [];
      let universalSpecialtyCodeMaps: EditTemplateOptionsDetailModel[] = [];
      codeMapSetService.GetEditTemplateOptionsCodeMapData(countyAndMarketCodeMapTypes, true, false, true).then((response: CodeMapSetModelWithActiveProjects[]) => {
          const universalMaps = response.map((universalCodemap) => {
          return ({
            activeProjects: universalCodemap.activeProjectsCount,
            clientId: universalCodemap.resource.clientId,
            modelId: universalCodemap.codeMapSetId,
            modelName: universalCodemap.resource.name,
            modelType: universalCodemap.codeMapType,
            dateArchived: universalCodemap.resource.dateArchived?.toString(),
            lastUpdated: universalCodemap.resource.dateLastUpdated?.toString()
          })
        })
        
        universalCountyCodeMaps = universalMaps.filter(x => x.modelType == CodeMapTypeEnum.CountyClassMap.toString() && x.clientId === 0);
        universalSpecialtyCodeMaps = universalMaps.filter(x => x.modelType == CodeMapTypeEnum.SpecialtyCodeMap.toString() && x.clientId === 0);
        setUniversalCountyCodeMaps(universalCountyCodeMaps);
        setUniversalSpecialtyCodeMaps(universalSpecialtyCodeMaps);     
      });

      let clientSpecificCountyCodeMaps: EditTemplateOptionsDetailModel[] = [];
      let clientSpecificSpecialtyCodeMaps: EditTemplateOptionsDetailModel[] = [];
      codeMapSetService.GetEditTemplateOptionsCodeMapData(countyAndMarketCodeMapTypes, true, true, true).then((response: CodeMapSetModelWithActiveProjects[]) => {       
        const maps = response.map((codemap) => {
        return ({
          activeProjects: codemap.activeProjectsCount,
          clientId: codemap.resource.clientId,
          modelId: codemap.codeMapSetId,
          modelName: codemap.resource.name,
          modelType: codemap.codeMapType,
          dateArchived: codemap.resource.dateArchived?.toString(),
          lastUpdated: codemap.resource.dateLastUpdated?.toString()
        })
      })
      
      clientSpecificCountyCodeMaps = maps.filter(x => x.modelType == CodeMapTypeEnum.CountyClassMap.toString() && x.clientId !== 0);
      clientSpecificSpecialtyCodeMaps = maps.filter(x => x.modelType == CodeMapTypeEnum.SpecialtyCodeMap.toString() && x.clientId !== 0);
      setClientSpecificCountyCodeMaps(clientSpecificCountyCodeMaps);            
      setClientSpecificSpecialtyCodeMaps(clientSpecificSpecialtyCodeMaps);    
    });

      let clientProviderData: EditTemplateOptionsDetailModel[] = [];
      providerSetService.GetEditTemplateOptionsProviderSetData(true, true, false, false).then((response: ProviderSetModelWithActiveProjects[]) => {
        clientProviderData = response.map((clientProviderData) => {
          return ({
            activeProjects: clientProviderData.activeProjectsCount,
            clientId: clientProviderData.resource.clientId,
            modelId: clientProviderData.providerSetId,
            modelName: clientProviderData.resource.name,
            modelType: clientProviderData.providerSetType,
            dateArchived: clientProviderData.resource.dateArchived?.toString(),
            lastUpdated: clientProviderData.resource.dateLastUpdated?.toString()
          })
        }) 
        setClientProviderData(clientProviderData);
      });

      let onlyUniversalProviderData: EditTemplateOptionsDetailModel[] = [];
      providerSetService.GetEditTemplateOptionsProviderSetData(true, false, false, true).then((response: ProviderSetModelWithActiveProjects[]) => {
       onlyUniversalProviderData = response.map((universalProviderData) => {
          return ({
            activeProjects: universalProviderData.activeProjectsCount,
            clientId: universalProviderData.resource.clientId,
            modelId: universalProviderData.providerSetId,
            modelName: universalProviderData.resource.name,
            modelType: universalProviderData.providerSetType,
            dateArchived: universalProviderData.resource.dateArchived?.toString(),
            lastUpdated: universalProviderData.resource.dateLastUpdated?.toString()
          })
        }) 
        setUniversalProviderData(onlyUniversalProviderData);
      });
      
      let clientMemberSetData: EditTemplateOptionsDetailModel[] = [];
      memberSetService.GetEditTemplateOptionsMemberSetData(true, true, true, false).then((response: MemberSetModelWithActiveProjects[]) => {
        const clientSpecificMemberSetData = response.map((membersetData) => {
          return ({
            activeProjects: membersetData.activeProjectsCount,
            clientId: membersetData.resource.clientId,
            modelId: membersetData.memberSetId,
            modelName: membersetData.resource.name,
            modelType: membersetData.memberSetType,
            dateArchived: membersetData.resource.dateArchived?.toString(),
            lastUpdated: membersetData.resource.dateLastUpdated?.toString()
          })
        })
        clientMemberSetData = clientSpecificMemberSetData.filter(x => x.clientId !== 0);
        setMemberSetData(clientMemberSetData);
      });

      let universalMemberSetData: EditTemplateOptionsDetailModel[] = [];
      memberSetService.GetEditTemplateOptionsMemberSetData(true, true, false, true).then((response: MemberSetModelWithActiveProjects[]) => {
        const allMemberSetData = response.map((uniMembersetData) => {
          return ({
            activeProjects: uniMembersetData.activeProjectsCount,
            clientId: uniMembersetData.resource.clientId,
            modelId: uniMembersetData.memberSetId,
            modelName: uniMembersetData.resource.name,
            modelType: uniMembersetData.memberSetType,
            dateArchived: uniMembersetData.resource.dateArchived?.toString(),
            lastUpdated: uniMembersetData.resource.dateLastUpdated?.toString()
          })
        })
        universalMemberSetData = allMemberSetData.filter(x => x.clientId === 0);
        setUniversalMemberSetData(universalMemberSetData);
      });
      /* oxlint-enable sort-keys */
    }
    if(refetchData) {
      fetchData();
      setRefetchData(false);
    }
  }, [refetchData]);

  useEffect(() => {
    const renderData = async () => {
      setClientCountyClassifications(await createCodeMapWafflesTable(clientSpecificCountyCodeMaps, "client county classification", "client_county_classification"));
      setUniversalCountyClassifications(await createCodeMapWafflesTable(universalCountyCodeMaps, "client county classification", "client_county_classification"));
      setClientMarkets(await createCodeMapWafflesTable(clientSpecificSpecialtyCodeMaps, "client county classification", "client_county_classification"));
      setUniversalMarkets(await createCodeMapWafflesTable(universalSpecialtyCodeMaps, "universal county classification", "universal_county_classification"));
      setClientMembers(await createCodeMapWafflesTable(memberSetData, "client members", "client_members"));
      setUniversalMembers(await createCodeMapWafflesTable(universalMemberSetData, "universal members", "universal_members"));
      setProviderSets(await createCodeMapWafflesTable(clientProviderData, "client provider sets", "client_provider_sets"));
      setUniversalProviderSets(await createCodeMapWafflesTable(universalProviderData, "client members", "client_members"));
    }

    if(universalCountyCodeMaps.length > 0)
    {
      renderData();
      setLoading(false);
    }
  }, [currentTab, clientSpecificCountyCodeMaps, universalCountyCodeMaps, clientSpecificSpecialtyCodeMaps, universalSpecialtyCodeMaps, memberSetData, universalMemberSetData, clientProviderData, universalProviderData]);

  const archiveResource = useCallback((modelResourceId: number) => {
    switch (currentTab) {
      case "County":
      case "Market":
        codeMapSetService.ArchiveCodeMapSetsAndCodeMapSetsVersions([modelResourceId]).then(() => setRefetchData(true));
        break;
      case "Member":
        memberSetService.archiveMemberSetAndMemberSetVersions([modelResourceId]).then(() => setRefetchData(true));
        break;
      case "Providers":
        providerSetService.archiveProviderSet(modelResourceId.toString()).then(() => setRefetchData(true));
        break;
      }
  }, [currentTab]);

  const unarchiveResource = useCallback((modelResourceId: number) => {
    switch (currentTab) {
      case "County":
      case "Market":
        codeMapSetService.ActivateCodeMapSetAndCodeMapSetVersions(modelResourceId).then(() => setRefetchData(true));
        break;
      case "Member":
        memberSetService.activateMemberSetAndMemberSetVersions(modelResourceId).then(() => setRefetchData(true));
        break;
      case "Providers":
        providerSetService.unarchiveProviderSet(modelResourceId.toString()).then(() => setRefetchData(true));
        break;
      }
  }, [currentTab]);

  const createCodeMapWafflesTable = async (data: EditTemplateOptionsDetailModel[], dataType: string, dataTypeId: string): Promise<EditTemplateOptionsTableData[]> => {
    /* oxlint-disable sort-keys */
    const rowData = data.map((row) => {
      return ({
        key: row.modelId.toString(),
        name: row.modelName,
        activeProjects: row.activeProjects,
        lastUpdated: row.lastUpdated,
        isArchived: !!row.dateArchived,     
        hide: (
          (!row.dateArchived &&
            <ModalLink
              classNames="qes-button-secondary danger"
              content={{
                body: `Are you sure you want to archive this ${dataType}?`,
                cancelButtonText: "Cancel",
                confirmButtonAction: () => archiveResource(row.modelId),
                confirmButtonText: "Archive",
                confirmButtonType: "primary-danger",
                title: `Archive Client County Classification`,
              }}
              testId={`${dataTypeId}-__archive-resource-modal`}
              hideBodyScroll={true}
              disabled = {((row.activeProjects > 0 ? true : false))}
            >
              Archive
            </ModalLink>) ||
          (row.dateArchived &&
            <ModalLink
              classNames="qes-button-secondary"
              content={{
                body: `Are you sure you want to unarchive this ${dataType}?`,
                cancelButtonText: "Cancel",
                confirmButtonAction: () => unarchiveResource(row.modelId),
                confirmButtonText: "Unarchive",
                confirmButtonType: "primary",
                title: `Unarchive Template Collection`,
              }}
              testId={`${dataTypeId}-__unarchive-resource-modal`}
              hideBodyScroll={true}
            >
              Unarchive
            </ModalLink>)
        ),
      });
      /* oxlint-enable */
    });
    return rowData;
  }

  const customSortCodeMapOptions = (data: EditTemplateOptionsTableData[], newSortedColumn: keyof EditTemplateOptionsTableData, sortDirection: SortDirections) => {
    if (sortDirection === SortDirections.None) {
      return data;
    }
    const multiplier = sortDirection === SortDirections.Asc ? 1 : -1;
    return data.sort((a, b) => (a[newSortedColumn] > b[newSortedColumn] ? 1 : -1) * multiplier);
  }

  const handleCountyPanelClick = useCallback(() => {
    setCurrentTab("County");
  }, []);

  const handleMarketPanelClick = useCallback(() => {
    setCurrentTab("Market");
  }, []);

  const handleMemberPanelClick = useCallback(() => {
    setCurrentTab("Member");
  }, []);

  const handleProvidersPanelClick = useCallback(() => {
    setCurrentTab("Providers");
  }, []);

  const handleCheckboxChangeClientCountyClass = useCallback((selectedCountyClassifications: string[]) => {
    setClientCountyClassificationsSelected(clientCountyClassifications.filter((x) => selectedCountyClassifications.includes(`${x.key}`) && x.activeProjects < 1 && !x.isArchived));
  }, [clientCountyClassifications]);

  const handleCheckboxChangeUniversalCountyClass = useCallback((selectedCountyClassifications: string[]) => {
    setUniversalCountyClassificationsSelected(universalCountyClassifications.filter((x) => selectedCountyClassifications.includes(`${x.key}`) && x.activeProjects < 1 && !x.isArchived));
  }, [universalCountyClassifications]);

  const handleCheckboxChangeClientMarket = useCallback((selectedMarkets: string[]) => {
    setClientMarketsSelected(clientMarkets.filter((x) => selectedMarkets.includes(`${x.key}`) && x.activeProjects < 1 && !x.isArchived));
  }, [clientMarkets]);

  const handleCheckboxChangeUniversalMarket = useCallback((selectedMarkets: string[]) => {
    setUniversalMarketsSelected(universalMarkets.filter((x) => selectedMarkets.includes(`${x.key}`) && x.activeProjects < 1 && !x.isArchived));
  }, [universalMarkets]);

  const handleCheckboxChangeClientMember = useCallback((selectedMembers: string[]) => {
    setClientMembersSelected(clientMembers.filter((x) => selectedMembers.includes(`${x.key}`) && x.activeProjects < 1 && !x.isArchived));
  }, [clientMembers]);

  const handleCheckboxChangeUniversalMember = useCallback((selectedMembers: string[]) => {
    setUniversalMembersSelected(universalMembers.filter((x) => selectedMembers.includes(`${x.key}`) && x.activeProjects < 1 && !x.isArchived));
  }, [universalMembers]);

  const handleCheckboxChangeClientProviderSet = useCallback((selectedProviderSets: string[]) => {
    setClientProviderSetsSelected(clientProviderSets.filter((x) => selectedProviderSets.includes(`${x.key}`) && x.activeProjects < 1 && !x.isArchived));
  }, [clientProviderSets]);

  const handleCheckboxChangeUniversalProviderSet = useCallback((selectedProviderSets: string[]) => {
    setUniversalProviderSetsSelected(universalProviderSets.filter((x) => selectedProviderSets.includes(`${x.key}`) && x.activeProjects < 1 && !x.isArchived));
  }, [universalProviderSets]);

  const archiveClientCounty = useCallback(() => {
    codeMapSetService.ArchiveCodeMapSetsAndCodeMapSetsVersions(clientCountyClassificationsSelected.map(classification => Number(classification.key))).then(() => setRefetchData(true));
    setClientCountyClassificationsSelected([]);
  }, [clientCountyClassificationsSelected]);

  const archiveUniversalCounty = useCallback(() => {
    codeMapSetService.ArchiveCodeMapSetsAndCodeMapSetsVersions(universalCountyClassificationsSelected.map(classification => Number(classification.key))).then(() => setRefetchData(true));
    setUniversalCountyClassificationsSelected([]);
  }, [universalCountyClassificationsSelected]);

  const archiveClientMarket = useCallback(() => {
    codeMapSetService.ArchiveCodeMapSetsAndCodeMapSetsVersions(clientMarketsSelected.map(market => Number(market.key))).then(() => setRefetchData(true));
    setClientMarketsSelected([]);
  }, [clientMarketsSelected]);

  const archiveUniversalMarket = useCallback(() => {
    codeMapSetService.ArchiveCodeMapSetsAndCodeMapSetsVersions(universalMarketsSelected.map(market => Number(market.key))).then(() => setRefetchData(true));
    setUniversalMarketsSelected([]);
  }, [universalMarketsSelected]);

  const archiveClientMember = useCallback(() => {
    memberSetService.archiveMemberSetAndMemberSetVersions(clientMembersSelected.map(member => Number(member.key)))
      .then(() => setRefetchData(true))
      .catch(() => {
        toast.error("Failed to archive member set(s). Some items may not have been archived.", { containerId: "z-index-9999" });
        setRefetchData(true);
      });
    setClientMembersSelected([]);
  }, [clientMembersSelected]);

  const archiveUniversalMember = useCallback(() => {
    memberSetService.archiveMemberSetAndMemberSetVersions(universalMembersSelected.map(member => Number(member.key)))
      .then(() => setRefetchData(true))
      .catch(() => {
        toast.error("Failed to archive member set(s). Some items may not have been archived.", { containerId: "z-index-9999" });
        setRefetchData(true);
      });
    setUniversalMembersSelected([]);
  }, [universalMembersSelected]);

  const archiveClientProvider = useCallback(() => {
    providerSetService.archiveProviderSetsAsync(clientProviderSetsSelected.map(provider => Number(provider.key))).then(() => setRefetchData(true));
    toast(<span>Archival job queued</span>, {
      containerId: "z-index-9999",
      type: "info",
    });
    setClientProviderSetsSelected([]);
  }, [clientProviderSetsSelected]);
  
  const archiveUniversalProvider = useCallback(() => {
    providerSetService.archiveProviderSetsAsync(universalProviderSetsSelected.map(provider => Number(provider.key))).then(() => setRefetchData(true));
    toast(<span>Archival job queued</span>, {
      containerId: "z-index-9999",
      type: "info"
    });
    setUniversalProviderSetsSelected([]);
  }, [universalProviderSetsSelected]);

  return (
    loading ? <LoadingSpinner /> :
    !universalCountyCodeMaps ? null :
    <>
      <div className="form-horizontal">
            <div className="row">
              <div className="col-12">
                <div className="tabs-style-2">
                  <ul className="nav nav-tabs">
                    <li className="nav-item"><a className={currentTab === "County" ? "nav-link active" : "nav-link"} href="#" role="tab" onClick={(e: React.MouseEvent) => preventDefaultMouseEvent(e, handleCountyPanelClick)}>County</a></li>
                    <li className="nav-item"><a className={currentTab === "Market" ? "nav-link active" : "nav-link"} href="#" role="tab" onClick={(e: React.MouseEvent) => preventDefaultMouseEvent(e, handleMarketPanelClick)}>Market</a></li>
                    <li className="nav-item"><a className={currentTab === "Member" ? "nav-link active" : "nav-link"} href="#" role="tab" onClick={(e: React.MouseEvent) => preventDefaultMouseEvent(e, handleMemberPanelClick)}>Member</a></li>
                    <li className="nav-item"><a className={currentTab === "Providers" ? "nav-link active" : "nav-link"} href="#" role="tab" onClick={(e: React.MouseEvent) => preventDefaultMouseEvent(e, handleProvidersPanelClick)}>Providers</a></li>
                  </ul>
                </div>

              </div>
              {currentTab === "County" &&
                  <>
                  <div><h3 className="lead flyout-heading-content w-100" style={{fontSize: '1rem', fontWeight: '600', margin: '20px 10px' }}>Edit County Classification Code Map</h3></div>
                  <div style={{textAlign: 'right', width: '100%'}}>
                    <ModalLink
                      classNames="qes-button-secondary ms-5"
                      content={{
                        body: "Are you sure you want to archive these client county classifications?",
                        cancelButtonText: "Go Back",
                        confirmButtonAction: archiveClientCounty,
                        confirmButtonText: "Archive",
                        confirmButtonType: "primary",
                        title: `Hide Client County Classifications`,
                      }}
                      testId="hide-client-county-classification__hide"
                      hideBodyScroll={true}
                      disabled={clientCountyClassificationsSelected?.length == 0}
                    >
                      Archive
                    </ModalLink>
                  </div>
                  <div style={{margin: '0px 10px', width: '100%'}}>
                    <Waffles<EditTemplateOptionsTableData>
                      columns={clientCountyColumnConfig}
                      selectedKeys={clientCountyClassificationsSelected.map((sr: EditTemplateOptionsTableData) => sr.key)}
                      showCheckboxes={true}
                      handleCheckboxChange={handleCheckboxChangeClientCountyClass}
                      customSort={customSortCodeMapOptions}
                      data={clientCountyClassifications}
                      enablePagination={true}
                      fullWidth={true}
                      loading={loading}
                      passThroughSorting={false}
                      testId="client-manage-templateCollections-waffles-table" />
                  </div>
                  { isUniversalTemplateManager && <><div style={{marginTop: '10px', textAlign: 'right', width: '100%'}}>
                    <ModalLink
                      classNames="qes-button-secondary ms-5"
                      content={{
                        body: "Are you sure you want to archive these client county classifications?",
                        cancelButtonText: "Go Back",
                        confirmButtonAction: archiveUniversalCounty,
                        confirmButtonText: "Archive",
                        confirmButtonType: "primary",
                        title: `Hide Client County Classifications`,
                      }}
                      testId="hide-client-county-classification__hide"
                      hideBodyScroll={true}
                      disabled={universalCountyClassificationsSelected?.length == 0}
                    >
                      Archive
                    </ModalLink>
                  </div>
                  <div style={{margin: '0px 10px', width: '100%'}}>
                    <Waffles<EditTemplateOptionsTableData>
                      columns={universalCountyColumnConfig}
                      showCheckboxes={true}
                      handleCheckboxChange={handleCheckboxChangeUniversalCountyClass}
                      selectedKeys={universalCountyClassificationsSelected.map((sr: EditTemplateOptionsTableData) => sr.key)}
                      customSort={customSortCodeMapOptions}
                      data={universalCountyClassifications}
                      enablePagination={true}
                      fullWidth={true}
                      loading={loading}
                      passThroughSorting={false}
                      testId="universal-manage-templateCollections-waffles-table" />
                  </div> </>}
                  </>
              }
              {currentTab === "Market" &&
                  <>
                  <div><h3 className="lead flyout-heading-content w-100" style={{fontSize: '1rem', fontWeight: '600', margin: '20px 10px' }}>Edit Market Specialty Code Map</h3></div>
                  <div style={{textAlign: 'right', width: '100%'}}>
                    <ModalLink
                      classNames="qes-button-secondary ms-5"
                      content={{
                        body: "Are you sure you want to archive these client markets?",
                        cancelButtonText: "Go Back",
                        confirmButtonAction: archiveClientMarket,
                        confirmButtonText: "Archive",
                        confirmButtonType: "primary",
                        title: `Hide Client Markets`,
                      }}
                      testId="hide-client-markets__hide"
                      hideBodyScroll={true}
                      disabled={clientMarketsSelected?.length == 0}
                    >
                      Archive
                    </ModalLink>
                  </div>
                  <div style={{margin: '0px 10px', width: '100%'}}>
                    <Waffles<EditTemplateOptionsTableData>
                      columns={clientMarketColumnConfig}
                      selectedKeys={clientMarketsSelected.map((sr: EditTemplateOptionsTableData) => sr.key)}
                      showCheckboxes={true}
                      handleCheckboxChange={handleCheckboxChangeClientMarket}
                      customSort={customSortCodeMapOptions}
                      data={clientMarkets}
                      enablePagination={true}
                      fullWidth={true}
                      loading={loading}
                      passThroughSorting={false}
                      testId="client-markets-waffles-table" />
                  </div>
                  { isUniversalTemplateManager && <><div style={{marginTop: '10px', textAlign: 'right', width: '100%'}}>
                    <ModalLink
                      classNames="qes-button-secondary ms-5"
                      content={{
                        body: "Are you sure you want to archive these universal markets?",
                        cancelButtonText: "Go Back",
                        confirmButtonAction: archiveUniversalMarket,
                        confirmButtonText: "Archive",
                        confirmButtonType: "primary",
                        title: `Hide Universal Markets`,
                      }}
                      testId="hide-universal-markets__hide"
                      hideBodyScroll={true}
                      disabled={universalMarketsSelected?.length == 0}
                    >
                      Archive
                    </ModalLink>
                  </div>
                  <div style={{margin: '0px 10px', width: '100%'}}>
                    <Waffles<EditTemplateOptionsTableData>
                      columns={universalMarketColumnConfig}
                      showCheckboxes={true}
                      handleCheckboxChange={handleCheckboxChangeUniversalMarket}
                      selectedKeys={universalMarketsSelected.map((sr: EditTemplateOptionsTableData) => sr.key)}
                      customSort={customSortCodeMapOptions}
                      data={universalMarkets}
                      enablePagination={true}
                      fullWidth={true}
                      loading={loading}
                      passThroughSorting={false}
                      testId="universal-markets-waffles-table" />
                  </div> </>}
                  </>
              }
              {currentTab === "Member" &&
                <>
                <div><h3 className="lead flyout-heading-content w-100" style={{fontSize: '1rem', fontWeight: '600', margin: '20px 10px' }}>Edit Member Population</h3></div>
                <div style={{textAlign: 'right', width: '100%'}}>
                  <ModalLink
                    classNames="qes-button-secondary ms-5"
                    content={{
                      body: "Are you sure you want to archive these client members?",
                      cancelButtonText: "Go Back",
                      confirmButtonAction: archiveClientMember,
                      confirmButtonText: "Archive",
                      confirmButtonType: "primary",
                      title: `Hide Client Members`,
                    }}
                    testId="hide-client-member__hide"
                    hideBodyScroll={true}
                    disabled={clientMembersSelected?.length == 0}
                  >
                    Archive
                  </ModalLink>
                </div>
                <div style={{margin: '0px 10px', width: '100%'}}>
                  <Waffles<EditTemplateOptionsTableData>
                    columns={clientMemberColumnConfig}
                    selectedKeys={clientMembersSelected.map((sr: EditTemplateOptionsTableData) => sr.key)}
                    showCheckboxes={true}
                    handleCheckboxChange={handleCheckboxChangeClientMember}
                    customSort={customSortCodeMapOptions}
                    data={clientMembers}
                    enablePagination={true}
                    fullWidth={true}
                    loading={loading}
                    passThroughSorting={false}
                    testId="client-manage-member-waffles-table" />
                </div>
                { isUniversalTemplateManager && <><div style={{marginTop: '10px', textAlign: 'right', width: '100%'}}>
                  <ModalLink
                    classNames="qes-button-secondary ms-5"
                    content={{
                      body: "Are you sure you want to archive these universal members?",
                      cancelButtonText: "Go Back",
                      confirmButtonAction: archiveUniversalMember,
                      confirmButtonText: "Archive",
                      confirmButtonType: "primary",
                      title: `Hide Universal Members`,
                    }}
                    testId="hide-universal-member__hide"
                    hideBodyScroll={true}
                    disabled={universalMembersSelected?.length == 0}
                  >
                    Archive
                  </ModalLink>
                </div>
                <div style={{margin: '0px 10px', width: '100%'}}>
                  <Waffles<EditTemplateOptionsTableData>
                    columns={universalMemberColumnConfig}
                    showCheckboxes={true}
                    handleCheckboxChange={handleCheckboxChangeUniversalMember}
                    selectedKeys={universalMembersSelected.map((sr: EditTemplateOptionsTableData) => sr.key)}
                    customSort={customSortCodeMapOptions}
                    data={universalMembers}
                    enablePagination={true}
                    fullWidth={true}
                    loading={loading}
                    passThroughSorting={false}
                    testId="universal-manage-member-waffles-table" />
                </div> </>}
                </>
              }
              {currentTab === "Providers" &&
                <>
                <div><h3 className="lead flyout-heading-content w-100" style={{fontSize: '1rem', fontWeight: '600', margin: '20px 10px' }}>Edit Provider Sets</h3></div>
                <div style={{textAlign: 'right', width: '100%'}}>
                  <ModalLink
                    classNames="qes-button-secondary ms-5"
                    content={{
                      body: "Are you sure you want to archive these client provider sets?",
                      cancelButtonText: "Go Back",
                      confirmButtonAction: archiveClientProvider,
                      confirmButtonText: "Archive",
                      confirmButtonType: "primary",
                      title: `Hide Client Provider Sets`,
                    }}
                    testId="hide-client-provider-set__hide"
                    hideBodyScroll={true}
                    disabled={clientProviderSetsSelected?.length == 0}
                  >
                    Archive
                  </ModalLink>
                </div>
                <div style={{margin: '0px 10px', width: '100%'}}>
                  <Waffles<EditTemplateOptionsTableData>
                    columns={clientProviderSetColumnConfig}
                    selectedKeys={clientProviderSetsSelected.map((sr: EditTemplateOptionsTableData) => sr.key)}
                    showCheckboxes={true}
                    handleCheckboxChange={handleCheckboxChangeClientProviderSet}
                    customSort={customSortCodeMapOptions}
                    data={clientProviderSets}
                    enablePagination={true}
                    fullWidth={true}
                    loading={loading}
                    passThroughSorting={false}
                    testId="client-manage-provider-sets-waffles-table" />
                </div>
                { isUniversalTemplateManager && <><div style={{marginTop: '10px', textAlign: 'right', width: '100%'}}>
                  <ModalLink
                    classNames="qes-button-secondary ms-5"
                    content={{
                      body: "Are you sure you want to archive these universal provider sets?",
                      cancelButtonText: "Go Back",
                      confirmButtonAction: archiveUniversalProvider,
                      confirmButtonText: "Archive",
                      confirmButtonType: "primary",
                      title: `Hide Universal Provider Sets`,
                    }}
                    testId="hide-universal-provider-sets__hide"
                    hideBodyScroll={true}
                    disabled={universalProviderSetsSelected?.length == 0}
                  >
                    Archive
                  </ModalLink>
                </div>
                <div style={{margin: '0px 10px', width: '100%'}}>
                  <Waffles<EditTemplateOptionsTableData>
                    columns={universalProviderSetsColumnConfig}
                    showCheckboxes={true}
                    handleCheckboxChange={handleCheckboxChangeUniversalProviderSet}
                    selectedKeys={universalProviderSetsSelected.map((sr: EditTemplateOptionsTableData) => sr.key)}
                    customSort={customSortCodeMapOptions}
                    data={universalProviderSets}
                    enablePagination={true}
                    fullWidth={true}
                    loading={loading}
                    passThroughSorting={false}
                    testId="universal-manage-provider-sets-waffles-table" />
                </div> </>}
                </>
              }
            </div>
          </div>
    </>
  );
};

export default EditTemplateOptionsFlyout;